/* eslint-disable no-unused-expressions */
import React from 'react'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { SrOnly } from 'pcln-design-system'
import DropdownCard from './DropdownCard'
import FetchTypeAhead from '../FetchTypeAhead'
import SearchItem from './SearchItem'
import RenderCurrentLocation from './RenderCurrentLocation'
import RenderRecentSearches from './RenderRecentSearches'
import INPUT_MIN_WIDTH from '../../../constants/dropdown'
import { ANALYTICS_CATEGORY_HOME } from '../../../constants/analytics'
import analytics from '../../../utils/analytics'

const Dropdown = ({
  isOpen,
  highlightedIndex,
  getItemProps,
  getMenuProps,
  searchKey,
  searchValue,
  clearItems,
  current,
  handleRequestCurrentLocationClick = () => {},
  showCurrentLocation = false,
  setHighlightedIndex,
  isMobile = false,
  typeAheadEndpoint,
  recentSearches = [],
  label = ''
}) => {
  const { pathname } = useRouter()
  const isHomepage = pathname === '/'
  const isFlightOrHotelTab =
    typeAheadEndpoint === 'flights' || typeAheadEndpoint === 'hotels'
  const gaLabel = label.includes('to') ? 'going to' : 'departing'

  return isOpen ? (
    <FetchTypeAhead
      searchValue={searchValue}
      searchKey={searchKey}
      typeAheadEndpoint={typeAheadEndpoint}
      onLoaded={() => {
        clearItems()
        setHighlightedIndex(0)
      }}
      render={({ items }) => {
        const isCardVisible = isOpen && items.length
        const isRecentSearchesVisible =
          isOpen && recentSearches.length && searchValue === ''
        return (
          <>
            {isCardVisible > 0 && (
              <SrOnly aria-live="polite">
                {items.length} number of results
              </SrOnly>
            )}
            <DropdownCard
              data-testid="typeahead-dropdown-card"
              boxShadowSize="lg"
              color="background.lightest"
              borderRadius={1}
              mt={1}
              role="listbox"
              width={1}
              minWidth={isMobile ? '' : INPUT_MIN_WIDTH}
              visible={
                isCardVisible || isRecentSearchesVisible || showCurrentLocation
              }
              {...getMenuProps({
                refKey: 'ref',
                isOpen
              })}
            >
              {showCurrentLocation && (
                <RenderCurrentLocation
                  current={current}
                  handleRequestCurrentLocationClick={
                    handleRequestCurrentLocationClick
                  }
                />
              )}
              {isRecentSearchesVisible ? (
                <RenderRecentSearches
                  getItemProps={getItemProps}
                  recentSearches={recentSearches}
                  highlightedIndex={highlightedIndex}
                  gaTracking={() => {
                    isHomepage && isFlightOrHotelTab
                      ? analytics.fireEvent({
                          category: ANALYTICS_CATEGORY_HOME,
                          action: `${typeAheadEndpoint} recent search`,
                          label: gaLabel
                        })
                      : null
                  }}
                />
              ) : null}
              {isCardVisible
                ? items.map((item, index) => (
                    <SearchItem
                      key={item.id}
                      isItemSelected={index === highlightedIndex}
                      index={index}
                      item={item}
                      getItemProps={getItemProps}
                    />
                  ))
                : null}
            </DropdownCard>
          </>
        )
      }}
    />
  ) : null
}

Dropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  highlightedIndex: PropTypes.number.isRequired,
  getItemProps: PropTypes.func.isRequired,
  getMenuProps: PropTypes.func.isRequired,
  searchKey: PropTypes.string.isRequired,
  searchValue: PropTypes.string.isRequired,
  clearItems: PropTypes.func.isRequired,
  setHighlightedIndex: PropTypes.func.isRequired,
  showCurrentLocation: PropTypes.bool,
  isMobile: PropTypes.bool,
  current: PropTypes.object,
  handleRequestCurrentLocationClick: PropTypes.func,
  typeAheadEndpoint: PropTypes.string,
  recentSearches: PropTypes.array,
  label: PropTypes.string
}

export default Dropdown
